import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/client';
import { useSidebarExtra } from '../context/SidebarExtraContext';
import { useCsvActions } from '../context/CsvActionsContext';
import { useT } from '../i18n';
import { Capability, Resource, CAPABILITY_DOMAINS, CRITICALITY_OPTIONS, DOMAIN_CAT } from '../types';
import { parseCsv } from '../utils/csvParser';
import { MousePointerClick, ClipboardList, Link } from 'lucide-react';

interface Contributor {
  id: number;
  name: string;
  initials: string;
}

interface LinkedResource extends Resource {
  criticalityScore: number | null;
  refinementText: string | null;
  resourceDescription?: string;
  contributors: Contributor[];
  contributorCount: number;
}

interface CapabilityRatingStatus {
  hasRatings: boolean;
  isComplete: boolean;
}

function CapabilityList({
  capabilities,
  selectedCapability,
  onSelect,
  capabilityRatingStatus,
}: {
  capabilities: Capability[];
  selectedCapability: Capability | null;
  onSelect: (cap: Capability) => void;
  capabilityRatingStatus: Record<number, CapabilityRatingStatus>;
}) {
  const groupedCapabilities = CAPABILITY_DOMAINS.reduce((acc, domain) => {
    acc[domain] = capabilities.filter((c) => c.domain === domain);
    return acc;
  }, {} as Record<string, Capability[]>);

  return (
    <div className="cap-list">
      {CAPABILITY_DOMAINS.map((domain) => {
        const domainCaps = groupedCapabilities[domain];
        if (domainCaps.length === 0) return null;
        return (
          <div key={domain} className="cap-domain">
            <div className="cap-domain-label">{domain}</div>
            {domainCaps.map((cap) => {
              const status = capabilityRatingStatus[cap.id];
              const dotClass = status?.isComplete ? 'complete' : status?.hasRatings ? 'partial' : '';
              return (
                <div
                  key={cap.id}
                  className={`cap-item ${selectedCapability?.id === cap.id ? 'active' : ''}`}
                  onClick={() => onSelect(cap)}
                >
                  <span className={`cap-item-dot ${dotClass}`} />
                  <span className="cap-item-title">{cap.title}</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export function WeightScoringPage() {
  const { t } = useT();
  const { setSidebarExtra } = useSidebarExtra();
  const { setCsvActions } = useCsvActions();

  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [selectedCapability, setSelectedCapability] = useState<Capability | null>(null);
  const [linkedResources, setLinkedResources] = useState<LinkedResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [capabilityRatingStatus, setCapabilityRatingStatus] = useState<Record<number, CapabilityRatingStatus>>({});

  // Debounce timers for refinement auto-save
  const refinementTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Register sidebar extra content
  useEffect(() => {
    setSidebarExtra(
      <CapabilityList
        capabilities={capabilities}
        selectedCapability={selectedCapability}
        onSelect={setSelectedCapability}
        capabilityRatingStatus={capabilityRatingStatus}
      />
    );
  }, [capabilities, selectedCapability, capabilityRatingStatus, setSidebarExtra]);

  // Cleanup sidebar on unmount
  useEffect(() => {
    return () => setSidebarExtra(null);
  }, [setSidebarExtra]);

  // Register CSV actions in topbar
  useEffect(() => {
    setCsvActions({
      uploadLabel: t('csv.uploadCsv'),
      onUpload: () => fileInputRef.current?.click(),
      downloadUrl: '/api/ratings/export-csv',
      downloadLabel: t('csv.exportCsv'),
      uploading: importing,
    });
    return () => setCsvActions(null);
  }, [importing, setCsvActions, t]);

  // Load capabilities and their rating status
  useEffect(() => {
    const loadCapabilitiesAndStatus = async () => {
      try {
        const caps = await api.getCapabilities() as Capability[];
        setCapabilities(caps);

        const statusMap: Record<number, CapabilityRatingStatus> = {};
        await Promise.all(
          caps.map(async (cap) => {
            const links = await api.getQualifiedResources(cap.id) as any[];
            if (links.length === 0) {
              statusMap[cap.id] = { hasRatings: false, isComplete: false };
              return;
            }

            const criticalityRatings = await api.getCriticalityRatings(cap.id) as any[];
            const criticalityMap = new Map(criticalityRatings.map((r: any) => [r.resource_id, r.score]));

            let hasAnyRating = false;
            let allComplete = true;

            for (const link of links) {
              const hasCriticality = criticalityMap.has(link.id);
              if (hasCriticality) {
                hasAnyRating = true;
              } else {
                allComplete = false;
              }
            }

            statusMap[cap.id] = {
              hasRatings: hasAnyRating,
              isComplete: hasAnyRating && allComplete,
            };
          })
        );

        setCapabilityRatingStatus(statusMap);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadCapabilitiesAndStatus();
  }, []);

  // Auto-select first capability (in sidebar domain order)
  useEffect(() => {
    if (capabilities.length > 0 && !selectedCapability) {
      for (const domain of CAPABILITY_DOMAINS) {
        const first = capabilities.find(c => c.domain === domain);
        if (first) { setSelectedCapability(first); break; }
      }
    }
  }, [capabilities]);

  const loadRatings = useCallback(async () => {
    if (!selectedCapability) return;

    try {
      const allLinks = await api.getQualifiedResources(selectedCapability.id) as any[];
      const criticalityRatings = await api.getCriticalityRatings(selectedCapability.id) as any[];
      const criticalityMap = new Map(criticalityRatings.map((r: any) => [r.resource_id, r.score]));

      const resources: LinkedResource[] = await Promise.all(
        allLinks.map(async (res: any) => {
          const refinement = await api.getUserRefinement(res.id, selectedCapability.id) as any;

          return {
            id: res.id,
            title: res.title,
            description: res.description || '',
            type: res.type,
            function: res.function,
            createdAt: '',
            criticalityScore: criticalityMap.get(res.id) ?? null,
            refinementText: refinement?.text ?? null,
            resourceDescription: res.description || '',
            contributors: res.contributors || [],
            contributorCount: res.contributorCount || 0,
          };
        })
      );

      setLinkedResources(resources);
    } catch (err) {
      console.error(err);
    }
  }, [selectedCapability]);

  useEffect(() => {
    loadRatings();
  }, [loadRatings]);

  const updateCapabilityStatus = (resources: LinkedResource[]) => {
    if (!selectedCapability) return;

    const hasAnyRating = resources.some(r => r.criticalityScore !== null);
    const allComplete = resources.length > 0 && resources.every(r => r.criticalityScore !== null);

    setCapabilityRatingStatus(prev => ({
      ...prev,
      [selectedCapability.id]: {
        hasRatings: hasAnyRating,
        isComplete: allComplete,
      },
    }));
  };

  const handleCriticalityChange = async (resourceId: number, score: number) => {
    if (!selectedCapability) return;

    const resource = linkedResources.find(r => r.id === resourceId);
    const isToggleOff = resource?.criticalityScore === score;
    const newScore = isToggleOff ? null : score;

    setSaving(prev => ({ ...prev, [resourceId]: true }));
    try {
      if (isToggleOff) {
        await api.setCriticalityRating(resourceId, selectedCapability.id, -1);
      } else {
        await api.setCriticalityRating(resourceId, selectedCapability.id, score);
      }

      const updatedResources = linkedResources.map((r) =>
        r.id === resourceId ? { ...r, criticalityScore: newScore } : r
      );
      setLinkedResources(updatedResources);
      updateCapabilityStatus(updatedResources);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(prev => ({ ...prev, [resourceId]: false }));
    }
  };

  const handleRefinementChange = async (resourceId: number, text: string) => {
    if (!selectedCapability) return;

    setSaving(prev => ({ ...prev, [resourceId]: true }));
    try {
      await api.setRefinement(resourceId, selectedCapability.id, text);
      setLinkedResources((prev) =>
        prev.map((r) => (r.id === resourceId ? { ...r, refinementText: text } : r))
      );
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(prev => ({ ...prev, [resourceId]: false }));
    }
  };

  const handleImportCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setImporting(true);
    try {
      const text = await file.text();
      const parsed = parseCsv(text);

      if (parsed.length === 0) {
        alert(t('weight.csv.empty'));
        return;
      }

      const rows = parsed.map(row => ({
        capability: row['Capability'] || '',
        resource: row['Resource'] || '',
        criticalityScore: row['Criticality Score'] || '',
      }));

      const result = await api.importCriticalityCsv(rows) as any;

      let msg = t('weight.import.imported')
        .replace('{n}', String(result.imported))
        .replace('{s}', result.imported !== 1 ? 's' : '');
      if (result.skipped > 0) msg += ' ' + t('weight.import.skipped').replace('{n}', String(result.skipped));
      if (result.errors?.length > 0) msg += '\n\n' + t('weight.import.warnings') + '\n' + result.errors.join('\n');
      alert(msg);

      // Reload current capability data
      loadRatings();
    } catch (err: any) {
      alert(t('weight.import.failed') + (err.message || t('weight.import.unknownError')));
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 200 }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="rating-page">
      {/* Hidden file input for CSV import (always in DOM for topbar button) */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleImportCsv}
      />
      {capabilities.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-state">
            <div className="empty-state-icon"><MousePointerClick /></div>
            <div className="empty-state-title">{t('weight.empty.noCaps.title')}</div>
            <div className="empty-state-desc">{t('weight.empty.noCaps.desc')}</div>
          </div>
        </div>
      ) : !selectedCapability ? (
        <div className="empty-state-card">
          <div className="empty-state">
            <div className="empty-state-icon"><MousePointerClick /></div>
            <div className="empty-state-desc">{t('weight.empty.selectCap')}</div>
          </div>
        </div>
      ) : linkedResources.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-state">
            <div className="empty-state-icon"><Link /></div>
            <div className="empty-state-title">{t('weight.empty.noResources.title')}</div>
            <div className="empty-state-desc">{t('weight.empty.noResources.desc')}</div>
          </div>
        </div>
      ) : (
        <>
          {/* Capability Detail Card */}
          <div className="detail-card detail-card--sticky">
            <div className="detail-card-header">
              <h4 className="detail-card-title">{selectedCapability.title}</h4>
              <div className="detail-card-header-tags">
                <span className={`meta-tag meta-tag--cat-${DOMAIN_CAT[selectedCapability.domain]}`}>{selectedCapability.domain}</span>
                <span className="meta-tag">{selectedCapability.nature}</span>
              </div>
            </div>
            <div className="detail-card-body">
              <p className="detail-card-description" style={{ maxWidth: Math.min(900, Math.max(300, Math.ceil((selectedCapability.description || '').length / 3 * 7))) }}>
                {selectedCapability.description || t('weight.noDescription')}
              </p>
              <span className="detail-card-meta">
                {t('weight.resourceCount')
                  .replace('{n}', String(linkedResources.length))
                  .replace('{s}', linkedResources.length !== 1 ? 's' : '')}
              </span>
            </div>
          </div>

          <div className="rating-page-content">
            <div className="flex flex-col gap-16">
              {(() => {
                const countGroups = new Map<number, LinkedResource[]>();
                for (const r of linkedResources) {
                  const count = r.contributorCount;
                  if (!countGroups.has(count)) countGroups.set(count, []);
                  countGroups.get(count)!.push(r);
                }
                const sortedCounts = Array.from(countGroups.keys()).sort((a, b) => b - a);

                return sortedCounts.map((count) => (
                  <div key={count}>
                    <div className="resource-group-header">
                      <span className="resource-group-count">{count}</span>
                      <span className="resource-group-label">
                        {count === 1 ? t('weight.selectedByOne') : t('weight.selectedByMany').replace('{n}', String(count))}
                      </span>
                    </div>
                    {countGroups.get(count)!.map((resource) => (
                      <div key={resource.id} className="rating-card-enhanced">
                        {/* Resource Header */}
                        <div className="rating-card-header">
                          <div className="rating-card-title-section">
                            <h4 className="rating-card-title">{resource.title}</h4>
                            <p className="rating-card-description">
                              {resource.resourceDescription || resource.description || t('weight.resourceNoDescription')}
                            </p>
                            <div className="resource-contributors">
                              {resource.contributors.map((c) => (
                                <span key={c.id} className="user-initials user-initials-sm" title={c.name}>{c.initials}</span>
                              ))}
                            </div>
                          </div>
                          <div className="rating-card-meta">
                            <span className="rating-card-type">{resource.type}</span>
                            <span className="rating-card-divider">|</span>
                            <span className="rating-card-function">{resource.function}</span>
                            {saving[resource.id] && <div className="spinner spinner-sm" />}
                          </div>
                        </div>

                        {/* Criticality Section */}
                        <div className="rating-section-enhanced">
                          <div className="rating-section-header">
                            <span className="rating-section-label">{t('weight.criticality.label')}</span>
                            <span className="rating-section-hint">{t('weight.criticality.hint')}</span>
                          </div>
                          <div className="rating-options-row">
                            {CRITICALITY_OPTIONS.map((option, idx) => (
                              <div key={option.value} className="rating-option-wrapper">
                                <button
                                  type="button"
                                  className={`rating-option-btn ${resource.criticalityScore === option.value ? 'selected' : ''} criticality-${idx}`}
                                  onClick={() => handleCriticalityChange(resource.id, option.value)}
                                >
                                  <span className="rating-option-value">{option.value.toFixed(2)}</span>
                                  <span className="rating-option-label">{option.label}</span>
                                  <span className="rating-option-info">
                                    i
                                    <span className="rating-option-tooltip">
                                      {t(`weight.criticalityTooltip.${option.value}`)}
                                    </span>
                                  </span>
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Refinement Section */}
                        <div className="rating-section-enhanced">
                          <div className="rating-section-header">
                            <span className="rating-section-label">{t('weight.comments.label')}</span>
                            <span className="rating-section-hint">{t('weight.comments.hint')}</span>
                          </div>
                          <textarea
                            className="rating-textarea"
                            placeholder={t('weight.comments.placeholder')}
                            value={resource.refinementText || ''}
                            onChange={(e) => {
                              const text = e.target.value;
                              const resId = resource.id;
                              setLinkedResources((prev) =>
                                prev.map((r) =>
                                  r.id === resId ? { ...r, refinementText: text } : r
                                )
                              );
                              // Debounced auto-save after 800ms of inactivity
                              clearTimeout(refinementTimers.current[resId]);
                              refinementTimers.current[resId] = setTimeout(() => {
                                handleRefinementChange(resId, text);
                              }, 800);
                            }}
                            onBlur={(e) => {
                              // Clear pending debounce and save immediately on blur
                              clearTimeout(refinementTimers.current[resource.id]);
                              handleRefinementChange(resource.id, e.target.value);
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
